import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validadores custom para Reactive Forms.
 * Todos retornan null si el valor es válido, o un objeto de error si no.
 */

// Proveedores de correo más comunes en Colombia: si el dominio escrito se parece
// mucho a uno de estos pero no es exacto, lo tratamos como typo (ej. "gamil.com").
const DOMINIOS_CONOCIDOS = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'icloud.com', 'live.com'];

function distanciaLevenshtein(a: string, b: string): number {
  const filas = a.length + 1;
  const columnas = b.length + 1;
  const dp: number[][] = Array.from({ length: filas }, () => new Array<number>(columnas).fill(0));

  for (let i = 0; i < filas; i++) dp[i][0] = i;
  for (let j = 0; j < columnas; j++) dp[0][j] = j;

  for (let i = 1; i < filas; i++) {
    for (let j = 1; j < columnas; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }

  return dp[a.length][b.length];
}

// "Casi igual, pero no exacto" a un dominio conocido: distancia de edición corta
// (typo de una o dos letras, ej. transponer "gmail" -> "gamil").
function esTypoDeDominioConocido(dominio: string): boolean {
  return DOMINIOS_CONOCIDOS.some(
    (conocido) => conocido !== dominio && distanciaLevenshtein(dominio, conocido) <= 2,
  );
}

/**
 * Años completos entre dos fechas (ajusta si `hasta` todavía no llega al día/mes de
 * aniversario de `desde` en su propio año). Compartido por `mayorDeEdad` (contra hoy) y
 * `fechaExpedicionMayorDeEdad` (contra la fecha de expedición del documento).
 */
function aniosEntreFechas(desde: Date, hasta: Date): number {
  let years = hasta.getFullYear() - desde.getFullYear();
  const yaCumplioEseAnio =
    hasta.getMonth() > desde.getMonth() ||
    (hasta.getMonth() === desde.getMonth() && hasta.getDate() >= desde.getDate());
  if (!yaCumplioEseAnio) years--;
  return years;
}

export const CELULAR_COLOMBIA_REGEX = /^3\d{9}$/;

export function esCelularColombiaValido(valor: string | null | undefined): boolean {
  return !!valor && CELULAR_COLOMBIA_REGEX.test(valor);
}

export const CustomValidators = {
  /**
   * Valida cédula de ciudadanía colombiana (CC): 6-10 dígitos numéricos
   */
  cedula(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const valid = /^\d{6,10}$/.test(control.value);
      return valid ? null : { cedula: 'Ingresa un número de cédula válido (6-10 dígitos)' };
    };
  },

  /**
   * Valida número de celular colombiano: 10 dígitos, empieza por 3
   */
  celularColombia(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const valid = CELULAR_COLOMBIA_REGEX.test(control.value);
      return valid ? null : { celular: 'Ingresa un número de celular válido (10 dígitos, empieza por 3)' };
    };
  },

  /**
   * Valida que el email tenga formato correcto. Distingue un formato groseramente
   * inválido (sin '@', con espacios, sin usuario) de un dominio mal escrito
   * (ej. "correo@.co"), porque el Figma pide un mensaje distinto para cada caso.
   */
  email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      if (!value.includes('@') || /\s/.test(value)) {
        return { emailInvalido: 'Ingresa un correo electrónico válido' };
      }

      const atIndex = value.lastIndexOf('@');
      const local = value.slice(0, atIndex);
      const domain = value.slice(atIndex + 1);
      if (!local) {
        return { emailInvalido: 'Ingresa un correo electrónico válido' };
      }

      const dominioValido = /^[^\s.]+(\.[^\s.]+)*\.[a-zA-Z]{2,}$/.test(domain);
      if (!dominioValido || esTypoDeDominioConocido(domain.toLowerCase())) {
        return { dominioInvalido: 'Revisa el dominio de tu correo' };
      }

      return null;
    };
  },

  /**
   * Valida que el campo no contenga solo espacios en blanco
   */
  noSoloEspacios(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const valid = control.value.trim().length > 0;
      return valid ? null : { soloEspacios: 'Este campo no puede estar vacío' };
    };
  },

  /**
   * Rechaza caracteres especiales no permitidos en direcciones (@, <, >, comillas, acentos agudos).
   */
  sinCaracteresEspeciales(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const valid = !/[@<>"'´`]/.test(control.value);
      return valid ? null : { caracteresEspeciales: 'Contiene caracteres no permitidos' };
    };
  },

  /**
   * Limita la cantidad de palabras (separadas por espacios) que puede tener el campo.
   * Usado en Nombres/Apellidos: la persona solo debe escribir su(s) nombre(s) o su(s)
   * apellido(s), no el nombre completo en un mismo campo.
   */
  maxPalabras(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const palabras = control.value.trim().split(/\s+/).filter(Boolean);
      return palabras.length > max ? { maxPalabras: true } : null;
    };
  },

  /**
   * Valida mayoría de edad (por defecto 18 años) contra la fecha de hoy, no contra
   * ninguna otra fecha del formulario (ej. fecha de expedición del documento).
   */
  mayorDeEdad(minAge = 18): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const birthDate = new Date(control.value);
      if (Number.isNaN(birthDate.getTime())) return null;

      const age = aniosEntreFechas(birthDate, new Date());
      return age >= minAge ? null : { menorDeEdad: true };
    };
  },

  /**
   * Valida que entre la fecha de nacimiento (control hermano, ej. `birthDate`) y la fecha
   * de expedición del documento (el propio control) haya mínimo `minAge` años de diferencia.
   * En Colombia la cédula de ciudadanía solo se expide siendo mayor de edad, así que una
   * fecha de expedición anterior a los 18 años del titular es inconsistente y bloquea el
   * flujo (HU39295: menor de edad cuando asegurado y tomador son la misma persona).
   *
   * No exige la fecha de nacimiento por su cuenta (eso ya lo hace `mayorDeEdad()` sobre ese
   * campo): si el hermano no existe o está vacío, este validador no reporta error.
   */
  fechaExpedicionMayorDeEdad(birthDateControlName = 'birthDate', minAge = 18): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const birthControl = control.parent?.get(birthDateControlName);
      if (!birthControl || !birthControl.value) return null;

      const issueDate = new Date(control.value);
      const birthDate = new Date(birthControl.value);
      if (Number.isNaN(issueDate.getTime()) || Number.isNaN(birthDate.getTime())) return null;

      const age = aniosEntreFechas(birthDate, issueDate);
      return age >= minAge ? null : { expedicionMenorDeEdad: true };
    };
  },
};
